import { useEffect, useRef, useState } from 'react';
import type { ComparisonPayload } from '@sleeved/lib';
import { useWsEvents } from '../hooks/useWsEvents';
import type { PlayerWidgetData, PlayerPhotoData } from '../types/widgets';
import { brandGradient, fonts, glass, glassBorder, glassText } from '../styles/overlayTokens';
import {
  type AnimPhase,
  INITIAL_COMPARISON,
  INITIAL_PLAYER,
  INITIAL_PHOTO,
  gameNameMap,
  gameLogoMap,
  computeSharedMax,
  computeLabelUnion,
  computeCoreCostLabelUnion,
  CuratedPlayerPanel,
  ChronoCoreDensePanel,
  DigimonDensePanel,
  GundamDensePanel,
  GrandArchiveDensePanel,
  VSCenter,
  selectDensePanel,
} from './DeckOverlayPanels';

// ---------------------------------------------------------------------------
// DeckCompareOverlay — main component
// Panel sub-components (CuratedPlayerPanel, ChronoCoreDensePanel, VSCenter) and
// all helpers/constants live in ./DeckOverlayPanels so they can be consumed by
// the upcoming DeckProfileOverlay without duplication.
// ---------------------------------------------------------------------------

export function DeckCompareOverlay() {
  const { data: compData } = useWsEvents<ComparisonPayload>('comparison', INITIAL_COMPARISON);
  const { data: playerData } = useWsEvents<PlayerWidgetData>('playerWidget', INITIAL_PLAYER);
  const { data: photoData } = useWsEvents<PlayerPhotoData>('playerPhoto', INITIAL_PHOTO);

  // Animation phase: 'hidden' → (rAF) → 'entering' | 'static'
  const [phase, setPhase] = useState<AnimPhase>(() =>
    new URLSearchParams(window.location.search).get('static') === 'true' ? 'static' : 'hidden',
  );

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  // Entrance: trigger after one rAF to prevent one-frame flash.
  useEffect(() => {
    if (phase !== 'hidden') return;
    const rafId = requestAnimationFrame(() => setPhase('entering'));
    return () => cancelAnimationFrame(rafId);
  }, [phase]);

  // Content update: when deck stats change while the overlay is already visible,
  // fade in the affected panel's content. Does NOT re-run the entrance sequence.
  const prevCompRef = useRef<ComparisonPayload>(INITIAL_COMPARISON);
  useEffect(() => {
    const prev = prevCompRef.current;
    prevCompRef.current = compData;

    // Skip on initial data load (INITIAL_COMPARISON sentinel) and in static mode
    // (?static=true sets phase to 'static', not 'entering'). 'entering' is the
    // steady state in normal mode, so content-update fades run there.
    if (phase !== 'entering' || prev === INITIAL_COMPARISON) return;

    const p1Changed = prev.p1 !== compData.p1;
    const p2Changed = prev.p2 !== compData.p2;
    if (!p1Changed && !p2Changed) return;

    const rafIds: number[] = [];
    const timerIds: ReturnType<typeof setTimeout>[] = [];

    const fadePanel = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.classList.remove('dcao-content-update');
      rafIds.push(requestAnimationFrame(() => el.classList.add('dcao-content-update')));
      timerIds.push(setTimeout(() => el.classList.remove('dcao-content-update'), 350));
    };

    if (p1Changed) fadePanel('dcao-content-p1');
    if (p2Changed) fadePanel('dcao-content-p2');

    return () => {
      rafIds.forEach(cancelAnimationFrame);
      timerIds.forEach(clearTimeout);
    };
  }, [compData, phase]);

  // ── Layout selection ────────────────────────────────────────────────────────
  // selectDensePanel returns the per-game dense panel component or null (falls back to CuratedPlayerPanel).
  const DensePanelComponent = selectDensePanel(compData.gameId);

  // Zone fill hidden for all games except grand-archive — GA zone fill renders inside GrandArchiveDensePanel
  // (NOT in VSCenter). grand-archive is now included so VSCenter never shows a duplicate zone fill row.
  const hideZoneFill = ['chrono-core', 'digimon', 'gundam', 'grand-archive'].includes(compData.gameId);

  // ── Shared scale computations (parent level — never per-panel) ──────────────
  // Mk Distribution (costCurve) — shared across both games/panels
  const sharedMax = computeSharedMax(compData.p1, compData.p2);
  const labelUnion = computeLabelUnion(compData.p1, compData.p2);

  // Core Cost Distribution (secondaryCurve) — ChronoCore only
  const coreCostSharedMax = Math.max(
    ...(compData.p1?.secondaryCurve ?? []).map(d => d.count),
    ...(compData.p2?.secondaryCurve ?? []).map(d => d.count),
    1,
  );
  const coreCostLabelUnion = computeCoreCostLabelUnion(compData.p1, compData.p2);

  // Damage Value heatmap — find by /damage/i label match (not array index)
  const dmgSharedMax = Math.max(
    ...(compData.p1?.additionalCurves?.find(c => /damage/i.test(c.label))?.data ?? []).map(d => d.count),
    ...(compData.p2?.additionalCurves?.find(c => /damage/i.test(c.label))?.data ?? []).map(d => d.count),
    1,
  );

  // Charge Value heatmap — find by /charge/i label match
  const chargeSharedMax = Math.max(
    ...(compData.p1?.additionalCurves?.find(c => /charge/i.test(c.label))?.data ?? []).map(d => d.count),
    ...(compData.p2?.additionalCurves?.find(c => /charge/i.test(c.label))?.data ?? []).map(d => d.count),
    1,
  );

  // ── Per-game shared-scale computations ─────────────────────────────────────
  // Digimon: level/form distribution (costCurve) — aliases of sharedMax/labelUnion.
  const digimonLevelSharedMax = sharedMax;
  const digimonLevelLabelUnion = labelUnion;

  // Gundam: level distribution (costCurve) — alias sharedMax; cost distribution (secondaryCurve)
  const gundamLevelSharedMax = sharedMax;
  const gundamLevelLabelUnion = labelUnion;
  const gundamCostSharedMax = Math.max(
    ...(compData.p1?.secondaryCurve ?? []).map(d => d.count),
    ...(compData.p2?.secondaryCurve ?? []).map(d => d.count),
    1,
  );
  // Re-uses computeCoreCostLabelUnion which operates on secondaryCurve — correct for Gundam cost labels
  const gundamCostLabelUnion = computeCoreCostLabelUnion(compData.p1, compData.p2);

  // Grand Archive: combined cost curve (memory + reserve series) — max over all series data
  const gaCombinedCostSharedMax = Math.max(
    ...(compData.p1?.combinedCostCurve ?? []).flatMap(s => s.data.map(d => d.count)),
    ...(compData.p2?.combinedCostCurve ?? []).flatMap(s => s.data.map(d => d.count)),
    1,
  );
  const gaCombinedCostLabelUnion = (() => {
    const allLabels = new Set<string>();
    for (const series of [
      ...(compData.p1?.combinedCostCurve ?? []),
      ...(compData.p2?.combinedCostCurve ?? []),
    ]) {
      for (const d of series.data) { allLabels.add(d.label); }
    }
    // Sort numerically (labels are cost numbers like "0", "1", "2", …)
    return [...allLabels].sort((a, b) => Number(a) - Number(b));
  })();

  const isHidden = phase === 'hidden';
  const gameName = gameNameMap[compData.gameId] ?? compData.gameId;

  // Under reduced motion: root fades in as a unit (150ms transition on opacity).
  // The dcao-*-enter classes still apply but their animations are overridden by the
  // @media (prefers-reduced-motion) rule in animations.css.

  return (
    <div
      aria-hidden="true"
      style={{
        width: 1920,
        height: 1080,
        position: 'relative',
        background: 'transparent',
        fontFamily: fonts.body,
        overflow: 'hidden',
        color: glassText.primary,
        // Hide root until rAF fires to prevent one-frame flash (static mode: always visible).
        opacity: isHidden && !prefersReducedMotion.current ? 0 : 1,
        // Reduced motion: fade the root in as a unit instead of staggered per-element.
        transition: prefersReducedMotion.current && !isHidden ? 'opacity 150ms ease-out' : undefined,
      }}
    >
      {/* Glass card 1840×1000 at 40px inset */}
      <div
        style={{
          position: 'absolute',
          inset: 40,
          background: glass.surfaceHeavy,
          borderRadius: 20,
          border: `1px solid ${glassBorder.light}`,
          boxShadow: '0 0 80px rgba(0,0,0,0.5), 0 8px 40px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top strip (100px) */}
        <div
          style={{
            height: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 40px',
            borderBottom: `1px solid ${glassBorder.subtle}`,
            flexShrink: 0,
          }}
        >
          {/* Left — Sleeved lockup (height 80 within the 100px top strip) */}
          <img src="/lockup.png" alt="Sleeved" style={{ height: 80, width: 'auto' }} />

          {/* Center — empty; reserved for future round/event label */}
          <span />

          {/* Right — game logo image (known games) or text pill fallback (unknown gameId) */}
          {gameLogoMap[compData.gameId] ? (
            <img
              src={gameLogoMap[compData.gameId]}
              alt={gameName}
              style={{ height: 80, width: 'auto', display: 'block' }}
            />
          ) : (
            <span
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: glassText.muted,
                background: glassBorder.subtle,
                borderRadius: 100,
                padding: '4px 14px',
              }}
            >
              {gameName}
            </span>
          )}
        </div>

        {/* Main content row */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            gap: 20,
            // Reduced vertical padding (was 32) to give panels ~28px more height —
            // buffer so keyword-heavy decks don't clip at the bottom.
            padding: '10px 40px',
            minHeight: 0,
          }}
        >
          {/* P1 panel — per-game dense panel (via selectDensePanel) or curated fallback */}
          {DensePanelComponent !== null ? (
            (() => {
              const common = { phase, reducedMotion: prefersReducedMotion.current };
              switch (compData.gameId) {
                case 'chrono-core':
                  return (
                    <ChronoCoreDensePanel
                      side="p1" stats={compData.p1} peerHasStats={compData.p2 !== null}
                      playerName={playerData.p1.name} archetype={playerData.p1.archetype}
                      pilotCardUrl={playerData.p1.pilotCardUrl} coreCardUrl={playerData.p1.coreCardUrl}
                      pilotName={playerData.p1.pilotName} coreName={playerData.p1.coreName}
                      weaponIdentity={playerData.p1.weaponIdentity} equipment={playerData.p1.equipment}
                      pilotLife={playerData.p1.pilotLife} coreShield={playerData.p1.coreShield}
                      coreCostSharedMax={coreCostSharedMax} coreCostLabelUnion={coreCostLabelUnion}
                      dmgSharedMax={dmgSharedMax} chargeSharedMax={chargeSharedMax}
                      {...common}
                    />
                  );
                case 'digimon':
                  return (
                    <DigimonDensePanel
                      side="p1" stats={compData.p1} peerHasStats={compData.p2 !== null}
                      playerName={playerData.p1.name} archetype={playerData.p1.archetype}
                      featuredCardImageUrl={playerData.p1.featuredCardImageUrl}
                      levelSharedMax={digimonLevelSharedMax} levelLabelUnion={digimonLevelLabelUnion}
                      {...common}
                    />
                  );
                case 'gundam':
                  return (
                    <GundamDensePanel
                      side="p1" stats={compData.p1} peerHasStats={compData.p2 !== null}
                      playerName={playerData.p1.name} archetype={playerData.p1.archetype}
                      featuredCardImageUrl={playerData.p1.featuredCardImageUrl}
                      levelSharedMax={gundamLevelSharedMax} levelLabelUnion={gundamLevelLabelUnion}
                      costSharedMax={gundamCostSharedMax} costLabelUnion={gundamCostLabelUnion}
                      {...common}
                    />
                  );
                case 'grand-archive':
                  return (
                    <GrandArchiveDensePanel
                      side="p1" stats={compData.p1} peerHasStats={compData.p2 !== null}
                      playerName={playerData.p1.name} archetype={playerData.p1.archetype}
                      gaIdentitySpiritUrl={playerData.p1.gaIdentitySpiritUrl}
                      gaIdentityChampionUrl={playerData.p1.gaIdentityChampionUrl}
                      combinedCostSharedMax={gaCombinedCostSharedMax}
                      combinedCostLabelUnion={gaCombinedCostLabelUnion}
                      {...common}
                    />
                  );
                default:
                  if (import.meta.env.DEV) console.warn(`[DeckCompareOverlay] selectDensePanel returned a panel for gameId "${compData.gameId}" but the inner switch has no case — falling back to CuratedPlayerPanel`);
                  return (
                    <CuratedPlayerPanel
                      side="p1"
                      stats={compData.p1}
                      peerHasStats={compData.p2 !== null}
                      playerName={playerData.p1.name}
                      archetype={playerData.p1.archetype}
                      photoUrl={photoData.p1Photo}
                      sharedMax={sharedMax}
                      labelUnion={labelUnion}
                      colorLabel={compData.colorLabel}
                      phase={phase}
                      reducedMotion={prefersReducedMotion.current}
                    />
                  );
              }
            })()
          ) : (
            <CuratedPlayerPanel
              side="p1"
              stats={compData.p1}
              peerHasStats={compData.p2 !== null}
              playerName={playerData.p1.name}
              archetype={playerData.p1.archetype}
              photoUrl={photoData.p1Photo}
              sharedMax={sharedMax}
              labelUnion={labelUnion}
              colorLabel={compData.colorLabel}
              phase={phase}
              reducedMotion={prefersReducedMotion.current}
            />
          )}

          {/* VS center */}
          <VSCenter comp={compData} phase={phase} hideZoneFill={hideZoneFill} />

          {/* P2 panel — per-game dense panel (via selectDensePanel) or curated fallback */}
          {DensePanelComponent !== null ? (
            (() => {
              const common = { phase, reducedMotion: prefersReducedMotion.current };
              switch (compData.gameId) {
                case 'chrono-core':
                  return (
                    <ChronoCoreDensePanel
                      side="p2" stats={compData.p2} peerHasStats={compData.p1 !== null}
                      playerName={playerData.p2.name} archetype={playerData.p2.archetype}
                      pilotCardUrl={playerData.p2.pilotCardUrl} coreCardUrl={playerData.p2.coreCardUrl}
                      pilotName={playerData.p2.pilotName} coreName={playerData.p2.coreName}
                      weaponIdentity={playerData.p2.weaponIdentity} equipment={playerData.p2.equipment}
                      pilotLife={playerData.p2.pilotLife} coreShield={playerData.p2.coreShield}
                      coreCostSharedMax={coreCostSharedMax} coreCostLabelUnion={coreCostLabelUnion}
                      dmgSharedMax={dmgSharedMax} chargeSharedMax={chargeSharedMax}
                      {...common}
                    />
                  );
                case 'digimon':
                  return (
                    <DigimonDensePanel
                      side="p2" stats={compData.p2} peerHasStats={compData.p1 !== null}
                      playerName={playerData.p2.name} archetype={playerData.p2.archetype}
                      featuredCardImageUrl={playerData.p2.featuredCardImageUrl}
                      levelSharedMax={digimonLevelSharedMax} levelLabelUnion={digimonLevelLabelUnion}
                      {...common}
                    />
                  );
                case 'gundam':
                  return (
                    <GundamDensePanel
                      side="p2" stats={compData.p2} peerHasStats={compData.p1 !== null}
                      playerName={playerData.p2.name} archetype={playerData.p2.archetype}
                      featuredCardImageUrl={playerData.p2.featuredCardImageUrl}
                      levelSharedMax={gundamLevelSharedMax} levelLabelUnion={gundamLevelLabelUnion}
                      costSharedMax={gundamCostSharedMax} costLabelUnion={gundamCostLabelUnion}
                      {...common}
                    />
                  );
                case 'grand-archive':
                  return (
                    <GrandArchiveDensePanel
                      side="p2" stats={compData.p2} peerHasStats={compData.p1 !== null}
                      playerName={playerData.p2.name} archetype={playerData.p2.archetype}
                      gaIdentitySpiritUrl={playerData.p2.gaIdentitySpiritUrl}
                      gaIdentityChampionUrl={playerData.p2.gaIdentityChampionUrl}
                      combinedCostSharedMax={gaCombinedCostSharedMax}
                      combinedCostLabelUnion={gaCombinedCostLabelUnion}
                      {...common}
                    />
                  );
                default:
                  if (import.meta.env.DEV) console.warn(`[DeckCompareOverlay] selectDensePanel returned a panel for gameId "${compData.gameId}" but the inner switch has no case — falling back to CuratedPlayerPanel`);
                  return (
                    <CuratedPlayerPanel
                      side="p2"
                      stats={compData.p2}
                      peerHasStats={compData.p1 !== null}
                      playerName={playerData.p2.name}
                      archetype={playerData.p2.archetype}
                      photoUrl={photoData.p2Photo}
                      sharedMax={sharedMax}
                      labelUnion={labelUnion}
                      colorLabel={compData.colorLabel}
                      phase={phase}
                      reducedMotion={prefersReducedMotion.current}
                    />
                  );
              }
            })()
          ) : (
            <CuratedPlayerPanel
              side="p2"
              stats={compData.p2}
              peerHasStats={compData.p1 !== null}
              playerName={playerData.p2.name}
              archetype={playerData.p2.archetype}
              photoUrl={photoData.p2Photo}
              sharedMax={sharedMax}
              labelUnion={labelUnion}
              colorLabel={compData.colorLabel}
              phase={phase}
              reducedMotion={prefersReducedMotion.current}
            />
          )}
        </div>

        {/* Bottom accent bar (brandGradient, 4px, same pattern as PlayerWidget) */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 4,
            background: brandGradient.horizontal,
          }}
        />
      </div>
    </div>
  );
}
